'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import supabaseClient from '@/lib/supabaseBrowser';

function normalizeRole(value: unknown) {
  return String(value || '').trim().toLowerCase();
}

export default function HomeRoleRedirect() {
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;

    const go = (path: '/teacher' | '/student') => {
      if (cancelled) return;
      router.replace(path);
      router.refresh();
    };

    (async () => {
      try {
        // Preferred path: use the server-side workspace resolver. It knows
        // both explicit roles and course-based teacher/student access, and it
        // returns the saved workspace preference only when still authorized.
        const workspaceResponse = await fetch('/api/auth/workspace', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'include',
        });
        const workspace = await workspaceResponse.json().catch(() => ({}));

        if (
          workspaceResponse.ok &&
          workspace?.ok === true &&
          workspace?.authenticated === true
        ) {
          if (workspace?.preference === 'student' && workspace?.canStudent) {
            go('/student');
            return;
          }

          if (workspace?.preference === 'teacher' && workspace?.canTeacher) {
            go('/teacher');
            return;
          }

          if (workspace?.canTeacher) {
            go('/teacher');
            return;
          }

          if (workspace?.canStudent) {
            go('/student');
            return;
          }
        }
      } catch {
        // Fall through to the browser-session path below.
      }

      try {
        // Browser fallback: this is intentionally retained because it fixed
        // browsers where middleware could not observe the active auth state.
        const supabase = supabaseClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const user = session?.user || null;
        if (user) {
          const email = String(user.email || '').trim().toLowerCase();
          const metadataRole = normalizeRole(user.user_metadata?.role);

          if (
            email.endsWith('@students.theaula.local') ||
            metadataRole === 'student'
          ) {
            go('/student');
            return;
          }

          const { data: roleRows } = await supabase
            .from('roles')
            .select('role')
            .eq('user_id', user.id);

          const roles = (roleRows || []).map((row: { role?: unknown }) =>
            normalizeRole(row.role)
          );

          if (
            roles.includes('teacher') ||
            roles.includes('admin') ||
            roles.includes('support') ||
            metadataRole === 'teacher'
          ) {
            go('/teacher');
            return;
          }

          if (roles.includes('student')) {
            go('/student');
            return;
          }
        }

        const response = await fetch('/api/student/roster-session', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'include',
        });
        const payload = await response.json().catch(() => ({}));
        if (payload?.ok && (payload?.activeCourseId || payload?.courseId)) {
          go('/student');
        }
      } catch {
        // Convenience-only redirect. Never block the public homepage on failure.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [router]);

  return null;
}
